import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { fetchApi } from '../lib/api';
import type {
  ScheduleTemplateSummary,
  ScheduleTemplateDetail,
  ScheduleTemplatePreview,
  ScheduleTemplateApplyResult,
} from '@repo/shared-types';
import type {
  CreateScheduleTemplateInput,
  RenameScheduleTemplateInput,
  PutScheduleTemplateEntriesInput,
} from '@repo/validation';

// "Mẫu lịch học" — a named draft of (slot × học viên) pairs. Mirrors
// useSlots.ts: same fetchApi, same toast-on-success/error shape, same
// `enabled`-as-second-argument convention (a fetch policy, not part of the
// request — folding it into the key would split the cache).

export function useScheduleTemplates(options?: { enabled?: boolean }) {
  return useQuery<ScheduleTemplateSummary[]>({
    queryKey: ['scheduleTemplates'],
    queryFn: () => fetchApi('/schedule-templates'),
    enabled: options?.enabled ?? true,
  });
}

export function useScheduleTemplate(id: string | null) {
  return useQuery<ScheduleTemplateDetail>({
    queryKey: ['scheduleTemplates', id],
    queryFn: () => fetchApi(`/schedule-templates/${id}`),
    enabled: id !== null,
  });
}

const inval = (qc: ReturnType<typeof useQueryClient>) => qc.invalidateQueries({ queryKey: ['scheduleTemplates'] });

export function useCreateScheduleTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateScheduleTemplateInput) =>
      fetchApi<ScheduleTemplateSummary>('/schedule-templates', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => { inval(qc); toast.success('Đã tạo mẫu'); },
    onError: (e) => toast.error(e.message || 'Không thể tạo mẫu'),
  });
}

export function useRenameScheduleTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...data }: RenameScheduleTemplateInput & { id: string }) =>
      fetchApi<ScheduleTemplateSummary>(`/schedule-templates/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: () => { inval(qc); toast.success('Đã đổi tên mẫu'); },
    onError: (e) => toast.error(e.message || 'Không thể đổi tên mẫu'),
  });
}

export function useDeleteScheduleTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => fetchApi<{ deleted: boolean }>(`/schedule-templates/${id}`, { method: 'DELETE' }),
    onSuccess: () => { inval(qc); toast.success('Đã xoá mẫu'); },
    onError: (e) => toast.error(e.message || 'Không thể xoá mẫu'),
  });
}

/** PUT /schedule-templates/:id/entries — whole-state replace, saved on every
 *  mutation like the rest of the app (no explicit "save" step in the draft
 *  editor). Optimistic: the draft grid must feel instant on every tick/drag,
 *  not wait a round trip.
 *
 *  Deliberately does NOT invalidate `['slots']` — a template save changes no
 *  reality, and invalidating would re-render the real grid for nothing. */
export function useSaveTemplateEntries(id: string) {
  const qc = useQueryClient();
  const key = ['scheduleTemplates', id] as const;

  return useMutation({
    mutationFn: (data: PutScheduleTemplateEntriesInput) =>
      fetchApi<ScheduleTemplateDetail>(`/schedule-templates/${id}/entries`, { method: 'PUT', body: JSON.stringify(data) }),
    onMutate: async (data) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<ScheduleTemplateDetail>(key);
      if (previous) {
        // Best-effort optimistic fill: carry over whatever we already know
        // from the current cache — studentName/enrollmentStatus (enrollment-
        // level, invariant across slots) and slotLabel/problem (slot-specific,
        // so keyed on the (slotId, enrollmentId) PAIR — an enrollment can sit
        // in the template at two different slots, and keying on enrollmentId
        // alone would bleed one slot's problem/label onto the other) — so the
        // grid (which only reads these fields, see overlayTemplateOnSlots)
        // keeps rendering correctly until onSettled's refetch has the real
        // thing.
        const byEnrollment = new Map(previous.entries.map((e) => [e.enrollmentId, e]));
        const byPair = new Map(previous.entries.map((e) => [`${e.slotId}:${e.enrollmentId}`, e]));
        const labelBySlot = new Map(previous.entries.map((e) => [e.slotId, e.slotLabel]));
        const optimisticEntries = data.entries.map((pair) => {
          const knownEnrollment = byEnrollment.get(pair.enrollmentId);
          const knownPair = byPair.get(`${pair.slotId}:${pair.enrollmentId}`);
          // Derived, not carried: an `orphaned` flag copied from a stale
          // entry could disagree with its own `problem` (the bug this
          // replaces) — `problem !== null` is the one thing that can't drift
          // from what this same object says.
          const problem = knownPair?.problem ?? null;
          return {
            slotId: pair.slotId,
            enrollmentId: pair.enrollmentId,
            studentId: knownEnrollment?.studentId ?? null,
            studentName: knownEnrollment?.studentName ?? null,
            slotLabel: labelBySlot.get(pair.slotId) ?? knownPair?.slotLabel ?? null,
            enrollmentStatus: knownEnrollment?.enrollmentStatus ?? null,
            orphaned: problem !== null,
            problem,
          };
        });
        qc.setQueryData<ScheduleTemplateDetail>(key, { ...previous, entries: optimisticEntries });
      }
      return { previous };
    },
    onError: (e, _vars, context) => {
      if (context?.previous) qc.setQueryData(key, context.previous);
      toast.error(e.message || 'Không thể lưu mẫu');
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: key });
      qc.invalidateQueries({ queryKey: ['scheduleTemplates'] });
    },
  });
}

/** POST /:id/preview — a `useMutation`, not a `useQuery`: the diff must be
 *  freshly computed against reality at the moment the apply dialog opens,
 *  never served from a cache that could be minutes stale. The dialog calls
 *  `.mutate()` itself from its own `onOpenChange(true)`. */
export function useTemplatePreview(id: string) {
  return useMutation({
    mutationFn: () => fetchApi<ScheduleTemplatePreview>(`/schedule-templates/${id}/preview`, { method: 'POST' }),
  });
}

/** POST /:id/apply. Deliberately carries no `onError` — the apply dialog is
 *  the only caller, and it must distinguish `OPEN_PERIOD` (re-render the
 *  hard stop) from every other failure (a bare toast); a hook-level toast
 *  here would double up with that on the one error the dialog most needs to
 *  render quietly. `onSuccess` only invalidates: the toast, dialog close and
 *  template-mode exit are the dialog's own `mutate()` callback, since they
 *  need `t()` and `setTemplateId(null)`, neither available in this file. */
export function useApplyTemplate(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { backupNamePrefix?: string }) =>
      fetchApi<ScheduleTemplateApplyResult>(`/schedule-templates/${id}/apply`, { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      // The real timetable just changed (joins/leaves) and the template's
      // own `appliedAt` moved — all four caches are now stale.
      qc.invalidateQueries({ queryKey: ['slots'] });
      qc.invalidateQueries({ queryKey: ['sessions'] });
      qc.invalidateQueries({ queryKey: ['enrollments'] });
      qc.invalidateQueries({ queryKey: ['scheduleTemplates'] });
    },
  });
}
